import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { Toaster } from 'sonner';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import MobileBottomNav from '@/components/MobileBottomNav';
import ProtectedRoute from '@/components/ProtectedRoute';
import AuthCallback from '@/components/AuthCallback';
import ScrollToTop from '@/components/ScrollToTop';

import Home from '@/pages/Home';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Artists from '@/pages/Artists';
import ArtistDetail from '@/pages/ArtistDetail';
import Onboarding from '@/pages/Onboarding';
import { Events, EventDetail } from '@/pages/EventsPages';
import { Castings, CastingDetail } from '@/pages/CastingsPages';
import Dashboard from '@/pages/Dashboard';
import Admin from '@/pages/Admin';
import PostCasting from '@/pages/PostCasting';
import PostEvent from '@/pages/PostEvent';
import KYCPage from '@/pages/KYC';
import Pricing from '@/pages/Pricing';
import PaymentSuccess from '@/pages/PaymentSuccess';
import Invite from '@/pages/Invite';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Disclaimer from '@/pages/Disclaimer';
import ForgotPassword from '@/pages/ForgotPassword';
import ReferralRedirect from '@/components/ReferralRedirect';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Hoisted so the Toaster doesn't receive a new object reference on every render.
const TOAST_OPTIONS = {
  style: {
    background: 'rgba(11, 16, 32, 0.9)',
    color: '#f8fafc',
    border: '1px solid rgba(255,255,255,0.1)',
    backdropFilter: 'blur(20px)',
  },
};

const AppShell = () => {
  const location = useLocation();
  // Synchronous OAuth session_id detection — handle BEFORE normal routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pb-20 md:pb-0">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/artists" element={<Artists />} />
          <Route path="/artists/:id" element={<ArtistDetail />} />
          <Route path="/u/:username" element={<ArtistDetail />} />
          <Route path="/events" element={<Events />} />
          <Route path="/events/:id" element={<EventDetail />} />
          <Route path="/castings" element={<Castings />} />
          <Route path="/castings/:id" element={<CastingDetail />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/r/:code" element={<ReferralRedirect />} />
          <Route
            path="/invite"
            element={<ProtectedRoute><Invite /></ProtectedRoute>}
          />
          <Route
            path="/post/casting"
            element={<ProtectedRoute><PostCasting /></ProtectedRoute>}
          />
          <Route
            path="/post/event"
            element={<ProtectedRoute><PostEvent /></ProtectedRoute>}
          />
          <Route
            path="/kyc"
            element={<ProtectedRoute><KYCPage /></ProtectedRoute>}
          />
          <Route
            path="/onboarding"
            element={<ProtectedRoute><Onboarding /></ProtectedRoute>}
          />
          <Route
            path="/dashboard"
            element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
          />
          <Route
            path="/admin"
            element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>}
          />
        </Routes>
      </main>
      <Footer />
      <MobileBottomNav />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppShell />
            <Toaster
              position="top-right"
              theme="system"
              toastOptions={TOAST_OPTIONS}
            />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
